import React from 'react';
import { BEV_FINISHED, BEV_FINISHED_LIST } from '../../bird-eye/mockData';
import type { BirdEyeFinishedSummary } from '../../bird-eye/boardTypes';
import type { BirdEyeFinishedPatient } from '../../bird-eye/types';
import { formatMoney } from '../../bird-eye/utils';
import { Avatar } from '../Avatar/Avatar';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Pill } from '../Pill/Pill';
import { ViewportOverlay } from '../Overlay/ViewportOverlay';

export interface BirdEyeFinishedModalProps {
  open: boolean;
  onClose: () => void;
  finishedSummary?: BirdEyeFinishedSummary;
  finishedList?: BirdEyeFinishedPatient[];
}

export function BirdEyeFinishedModal({
  open,
  onClose,
  finishedSummary,
  finishedList,
}: BirdEyeFinishedModalProps) {
  const summary = finishedSummary ?? { total: BEV_FINISHED.total, production: BEV_FINISHED.production };
  const list = finishedList ?? BEV_FINISHED_LIST;
  return (
    <ViewportOverlay open={open} onClose={onClose}>
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className="flex max-h-[min(calc(100vh-48px),720px)] w-[min(720px,calc(100vw-48px))] flex-col overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-3)]"
      >
        <div className="flex items-center gap-[13px] border-b border-[var(--cream-2)] bg-[var(--cream-0)] px-[22px] py-[18px]">
          <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[var(--success-tint)] text-[var(--success)]">
            <Icon kind="check" size={18} stroke={2.4} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-[var(--font-display)] text-lg font-semibold tracking-[-0.015em] text-[var(--ink-0)]">
              Finished patients
            </div>
            <div className="mt-px text-[12.5px] text-[var(--ink-3)]">
              {summary.total} completed · {formatMoney(summary.production)} production
            </div>
          </div>
          <IconButton icon="x" size={34} onClick={onClose} label="Close" />
        </div>
        <div className="be-scroll flex-1 overflow-y-auto px-2.5 py-1.5">
          {list.map((f, i) => (
            <div
              key={`${f.name}-${f.time}`}
              className="flex items-center gap-[13px] px-3 py-3"
              style={{
                borderBottom:
                  i < list.length - 1 ? '1px solid var(--cream-2)' : undefined,
              }}
            >
              <Avatar name={f.name} hue="var(--success)" size={36} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[var(--ink-0)]">{f.name}</div>
                <div className="mt-0.5 flex items-center gap-[7px] font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]">
                  <Icon kind="clock" size={11} stroke={1.8} />
                  {f.time}
                  <span className="text-[var(--ink-4)]">·</span>
                  <span className="font-semibold text-[var(--ink-1)]">{formatMoney(f.value)}</span>
                </div>
              </div>
              {f.ins ? (
                <Pill tone="success" className="gap-[5px] px-[11px] py-1">
                  <Icon kind="shield-check" size={10} stroke={1.9} />
                  Insurance
                </Pill>
              ) : (
                <span className="inline-flex items-center gap-[5px] rounded-[var(--r-pill)] bg-[var(--terracotta-tint)] px-[11px] py-1 text-[11.5px] font-semibold text-[var(--terracotta)]">
                  <Icon kind="shield" size={10} stroke={1.9} />
                  No insurance
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </ViewportOverlay>
  );
}
